import {
  User,
  UserWithPasswordFlag,
} from '@EHSNavigator/backend';
import clsx from 'clsx';
import React from 'react';
import DropDown from '../DropDown';
import { useDispatch } from 'react-redux';
import { toggleModal } from '../../reducers/modalToggle';
import { useLocalStorage } from '@uidotdev/usehooks';
import { UserWithOrgContext } from '../../types/user';

interface UserManagementRowProps {
  otherClasses?: string;
  user: UserWithPasswordFlag;
  index: any;
  lastTwoIndex: User[];
  setEditValues: (args: any) => void;
  setDeleteValues: (args: any) => void;
  handleArchive: (id: string) => void;
  isArchiveSubmitting: boolean;
  handleResendSetPassword: (id: string) => void;
  isResendSubmitting: boolean;
}

const UserManagementRow: React.FC<UserManagementRowProps> = ({
  otherClasses,
  user,
  index,
  lastTwoIndex,
  setEditValues,
  setDeleteValues,
  handleArchive,
  handleResendSetPassword,
  isResendSubmitting,
  isArchiveSubmitting,
}) => {
  const userManagementRowClasses = clsx(
    otherClasses,
    'border-b-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b py-5 relative items-center',
  );

  const [userInfo] = useLocalStorage<UserWithOrgContext | null>(
    'userInfo',
    null,
  );
  const isOrgContext = !!(userInfo?.orgContext);
  const dispatch = useDispatch();

  const isCurrentUser = userInfo?.user?.id === user.id;
  const isAdmin = userInfo?.user?.role?.name === 'Admin';

  const options = [
    {
      label: 'Edit',
      onClick: (args: any) => {
        setEditValues(args);
        dispatch(toggleModal('change-user-role'));
      },
      onOptionClickArgs: user,
      hidden: user?.isArchived ? true : false,
    },

    {
      label: user?.isArchived ? 'Unarchive' : 'Archive',
      onClick: (args: any) => handleArchive(args?.id),
      onOptionClickArgs: user,
      hidden: isCurrentUser || (isOrgContext ? ['Owner']?.includes(user?.role?.name) : false), // hide if current user or owner
      isLoading: isArchiveSubmitting,
    },

    {
      label: 'Resend Invite',
      onClick: (args: any) => handleResendSetPassword(args?.id),
      onOptionClickArgs: user,
      hidden: isCurrentUser || user?.isArchived || user?.isVerified || (isOrgContext ? ['Owner']?.includes(user?.role?.name) : false),
      isLoading: isResendSubmitting,
    },

    {
      label: 'Delete',
      onClick: (args: any) => {
        setDeleteValues(args);
        dispatch(toggleModal('delete-user'));
      },
      onOptionClickArgs: user,
      hidden: isCurrentUser || (isOrgContext ? ['Owner']?.includes(user?.role?.name) : false),
    },
  ];


  return (
    <div className={userManagementRowClasses} data-testid="user-management-row">
      <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-1 text-sm leading-4 font-bold lg:text-base lg:leading-5 lg:font-normal">
        {index}
      </p>
      <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal break-words lg:text-base lg:leading-5">
        {user?.employeeId ? user?.employeeId : '-'}
      </p>
      <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal lg:text-base lg:leading-5">
        {user?.fullName}
      </p>
      <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-3 text-sm leading-4 font-normal break-words lg:text-base lg:leading-5">
        {user?.email}
      </p>
      <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal lg:text-base lg:leading-5">
        {user?.department?.name ? user?.department?.name : '-'}
      </p>
      <div className="col-span-2 flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input text-sm leading-4 font-normal lg:text-base lg:leading-5">
            {user?.role?.name}
          </p>
          {user?.isArchived && (
            <span className="font-inter-variable bg-brand-byzantine rounded px-2 py-0.5 text-xs leading-4 font-normal text-white">
              Archived
            </span>
          )}
        </div>
        <DropDown
          options={options}
          disabled={
            isAdmin &&
            !isCurrentUser &&
            ['Admin', 'Owner']?.includes(user?.role?.name)
          }
          otherClasses="flex items-center"
          additionalClasses={clsx(
            '',
            lastTwoIndex.some((node) => node?.id === user?.id)
              ? 'bottom-10 top-auto max-w-36'
              : 'max-w-36',
          )}
        />
      </div>
    </div>
  );
};

export default UserManagementRow;
